import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ArrowRight, Phone } from 'lucide-react';

const Hero = () => {
  const [textRevealed, setTextRevealed] = useState([]);
  const [line1Visible, setLine1Visible] = useState(false);
  const [line2Visible, setLine2Visible] = useState(false);
  const [cursorActive, setCursorActive] = useState(false);
  const bgRef = useRef(null);
  const sectionRef = useRef(null);

  const handleScroll = useCallback(() => {
    if (!bgRef.current || !sectionRef.current) return;
    if (window.innerWidth < 768) return;
    const rect = sectionRef.current.getBoundingClientRect();
    if (rect.bottom < 0) return;
    const offset = window.scrollY * 0.35;
    bgRef.current.style.transform = `translate3d(0, ${offset}px, 0)`;
  }, []);

  useEffect(() => {
    // Headline animation: line 1 fades in, then line 2 after delay
    setTimeout(() => setLine1Visible(true), 300);
    setTimeout(() => { setCursorActive(true); }, 400);
    setTimeout(() => setLine2Visible(true), 1000);
    setTimeout(() => setCursorActive(false), 2200);

    // Staggered reveal for remaining content
    const delays = [1200, 1500, 1800, 2100];
    delays.forEach((delay, index) => {
      setTimeout(() => {
        setTextRevealed(prev => [...prev, index]);
      }, delay);
    });

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section ref={sectionRef} id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Premium Real Estate Background - High-rise Building */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Building Image with parallax + slow zoom */}
        <div 
          ref={bgRef}
          className="absolute inset-0 gpu-accelerated bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_builder-solutions-1/artifacts/6dc2mlq8_pune-4067841_1280.jpg')`,
            backgroundPosition: 'center center',
            backgroundSize: 'cover',
            top: '-15%',
            bottom: '-15%',
            willChange: 'transform'
          }}
        ></div>
        
        {/* Soft dark gradient overlay - static, stronger on left for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/35"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/25"></div>
      </div>

      {/* Subtle light streak */}
      <div className="light-streak"></div>

      {/* Minimal gradient orbs - subtle depth */}
      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-white/2 rounded-full opacity-10 animate-pulse"></div>

      {/* Hero Content - Clean & Left-aligned */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-36 relative z-10">
        <div className="max-w-3xl">
          {/* Main Headline - Two-step reveal */}
          <div className="mb-14">
            <h1 className="text-6xl sm:text-7xl lg:text-[5.5rem] font-extrabold leading-[1.05] tracking-[-0.03em]" data-testid="hero-headline">
              <span
                className="block text-white transition-all duration-700 ease-out"
                style={{
                  opacity: line1Visible ? 1 : 0,
                  transform: line1Visible ? 'translate3d(0,0,0)' : 'translate3d(0,18px,0)',
                }}
              >
                Struggling to Sell
                <br />
                Inventory
                <span className={`inline-block ${cursorActive ? 'hero-cursor-pulse' : ''}`}>?</span>
              </span>
              <span
                className="block mt-8 transition-all duration-700 ease-out bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent"
                style={{
                  opacity: line2Visible ? 1 : 0,
                  transform: line2Visible ? 'translate3d(0,0,0)' : 'translate3d(0,18px,0)',
                }}
              >
                We Fix That.
              </span>
            </h1>
          </div>

          {/* Subtext */}
          <div className={`text-reveal ${textRevealed.includes(0) ? 'revealed' : ''} mb-16`}>
            <p className="text-lg md:text-xl text-gray-300/90 leading-[1.8] font-light max-w-2xl tracking-[-0.01em]" data-testid="hero-subtext">
              Nova Homes helps builders generate qualified leads, increase site visits, and convert inventory into confirmed bookings.
            </p>
          </div>

          {/* Premium CTAs */}
          <div className={`text-reveal ${textRevealed.includes(1) ? 'revealed' : ''} flex flex-col sm:flex-row gap-4`}>
            {/* Primary CTA - Solid */}
            <button
              onClick={scrollToContact}
              className="group relative inline-flex items-center justify-center space-x-3 bg-white text-black px-10 py-4.5 rounded-lg hover:bg-gray-100 transition-all duration-300 btn-cinematic text-base font-bold shadow-2xl overflow-hidden"
              data-testid="partner-button"
            >
              <span className="relative z-10">Partner With Us</span>
              <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            
            {/* Secondary CTA - Ghost/Outline */}
            <button
              onClick={() => {
                const element = document.querySelector('#services');
                if (element) {
                  const offset = 100;
                  const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                  window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
                }
              }}
              className="inline-flex items-center justify-center space-x-3 bg-transparent text-white/70 px-10 py-4.5 rounded-lg border border-white/15 hover:text-white hover:border-white/30 hover:bg-white/5 transition-all duration-300 text-base font-medium"
              data-testid="sales-support-button"
            >
              <Phone className="w-4 h-4" />
              <span>Get Sales Support</span>
            </button>
          </div>

          {/* Trust Line */}
          <div className={`text-reveal ${textRevealed.includes(1) ? 'revealed' : ''} mt-7`}>
            <p className="text-sm text-white/60 font-normal tracking-wide" data-testid="hero-trust-line">
              Trusted by builders across Pune&nbsp;&nbsp;|&nbsp;&nbsp;&#8377;91 Cr+ Inventory Moved
            </p>
          </div>

          {/* Quick Stats - Premium Typography */}
          <div className={`text-reveal ${textRevealed.includes(2) ? 'revealed' : ''} mt-16 pt-10 border-t border-white/10`}>
            <div className="flex flex-wrap gap-x-16 gap-y-8">
              <div className="flex items-baseline space-x-3">
                <span className="text-5xl font-bold text-white tracking-tight">98%</span>
                <span className="text-[15px] text-gray-400 font-medium tracking-wide">Client Satisfaction</span>
              </div>
              <div className="flex items-baseline space-x-3">
                <span className="text-5xl font-bold text-white tracking-tight">7+</span>
                <span className="text-[15px] text-gray-400 font-medium tracking-wide">Successful Projects</span>
              </div>
              <div className="flex items-baseline space-x-3">
                <span className="text-5xl font-bold text-white tracking-tight">100%</span>
                <span className="text-[15px] text-gray-400 font-medium tracking-wide">Inventory Clearance</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade to next section */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10"></div>
    </section>
  );
};

export default Hero;
